<script setup lang="ts">
/**
 * Presentational refund-queue list. Receives rows + pagination +
 * filter-count via props; emits row-click / update:page / filter-click.
 * No queries, no mutations, no router pushes — orchestration lives in
 * the container.
 */
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import DataTableV2, { type TableColumn } from '@uiKit/components/DataTableV2.vue'
import CopyButton from '@uiKit/components/CopyButton.vue'
import StatusBadge from '@uiKit/components/StatusBadge.vue'
import { formatDate, formatMoney } from '@shared/utils'
import type { RefundRow } from '../types/refundsTypes'

defineProps<{
  rows: RefundRow[]
  loading: boolean
  total: number
  page: number
  pageSize: number
  filterCount: number
  emptyTitle?: string
  emptySubtitle?: string
  title?: string
  subtitle?: string
  search?: string
}>()

const emit = defineEmits<{
  (e: 'update:page', value: number): void
  (e: 'update:pageSize', value: number): void
  (e: 'row-click', row: RefundRow): void
  (e: 'filter-click'): void
  (e: 'update:search', value: string): void
}>()

const columns: TableColumn[] = [
  { key: 'investor', label: 'INVESTOR', width: 'minmax(220px, 1.6fr)' },
  { key: 'order', label: 'ORDER REF', width: 'minmax(200px, 1.4fr)' },
  { key: 'amount', label: 'AMOUNT', width: 'minmax(160px, 1fr)', align: 'right' },
  { key: 'requestedAt', label: 'REQUESTED', width: 'minmax(140px, 1fr)' },
]

function formatAmount(row: RefundRow): string {
  return formatMoney(row.currencyCode, row.amount)
}
</script>

<template>
  <DataTableV2
    :columns="columns"
    :rows="rows"
    :loading="loading"
    row-key="id"
    :title="title"
    :subtitle="subtitle"
    :search="search"
    search-placeholder="Search by investor name…"
    :local-search="false"
    :local-pagination="false"
    :filterable="true"
    :filter-count="filterCount"
    :page="page"
    :page-size="pageSize"
    :total="total"
    :url-sync="true"
    page-size-storage-key="treasury.refunds.pageSize"
    clickable
    :empty-title="emptyTitle ?? 'No refund requests'"
    :empty-subtitle="emptySubtitle ?? 'Requests matching your filters will appear here.'"
    @update:page="emit('update:page', $event)"
    @update:page-size="emit('update:pageSize', $event)"
    @update:search="emit('update:search', $event)"
    @filter-click="emit('filter-click')"
    @row-click="emit('row-click', $event as RefundRow)"
  >
    <template #cell-investor="{ row }">
      <Box flex direction="col">
        <Text type="body" weight="semibold">{{ (row as RefundRow).investorName || '—' }}</Text>
        <Text type="caption" color="muted">
          {{ (row as RefundRow).investorEmail || '—' }}
        </Text>
      </Box>
    </template>

    <template #cell-order="{ row }">
      <Box flex direction="col" gap="1" align="start">
        <Box v-if="(row as RefundRow).orderReference" flex align="center" gap="2">
          <Text type="body-sm" color="muted">{{ (row as RefundRow).orderReference }}</Text>
          <CopyButton
            :value="(row as RefundRow).orderReference"
            label="Copy"
            copied-label="Copied"
          />
        </Box>
        <Text v-else type="body-sm" color="muted">—</Text>
        <StatusBadge
          :color="(row as RefundRow).orderCancelledAt ? 'neutral' : 'danger'"
          :label="(row as RefundRow).orderCancelledAt ? 'Cancelled' : 'Rejected'"
        />
      </Box>
    </template>

    <template #cell-amount="{ row }">
      <Text
        type="body-sm"
        weight="semibold"
        align="right"
        style="font-variant-numeric: tabular-nums"
      >
        {{ formatAmount(row as RefundRow) }}
      </Text>
    </template>

    <template #cell-requestedAt="{ row }">
      <Text type="body-sm" color="muted">{{ formatDate((row as RefundRow).requestedAt) }}</Text>
    </template>
  </DataTableV2>
</template>
